import { useState, useSyncExternalStore } from "react";
import { useT } from "@/lib/i18n";
import { Volume2, Turtle, RotateCcw, Square, Play, Pause, X } from "lucide-react";
import {
  speak,
  speakSlow,
  stop as stopAudio,
  pause as pauseAudio,
  resume as resumeAudio,
  setSpeed,
  SPEEDS,
  IDLE_STATE,
  getPlaybackState,
  subscribePlayback,
} from "@/services/audioService";
import { cn } from "@/lib/utils";

export function useSpeaker() {
  const [active, setActive] = useState(false);
  const run = (fn: typeof speak, text: string) =>
    fn(text, { onStart: () => setActive(true), onEnd: () => setActive(false) });
  return {
    active,
    play: (t: string) => run(speak, t),
    slow: (t: string) => run(speakSlow, t),
    stop: () => {
      stopAudio();
      setActive(false);
    },
  };
}

/** Animated rings shown while a speaker is playing. */
function SoundRings() {
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0">
      <span className="sound-ring absolute inset-0 rounded-full bg-primary/40" />
      <span className="sound-ring absolute inset-0 rounded-full bg-primary/30 [animation-delay:0.4s]" />
    </span>
  );
}

export function AudioButton({
  text,
  size = "md",
  className,
}: {
  text: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const t = useT();
  const { active, play } = useSpeaker();
  const s = size === "lg" ? "h-16 w-16" : size === "sm" ? "h-9 w-9" : "h-11 w-11";
  return (
    <button
      type="button"
      aria-label={t(`${text} सुनें`, `Listen: ${text}`)}
      aria-pressed={active}
      onClick={(e) => {
        e.stopPropagation();
        play(text);
      }}
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#94d8ff] to-[#3aa9ea] text-white shadow-[0_8px_18px_-8px_rgb(58_169_234/0.8)] transition hover:scale-105 active:scale-95",
        s,
        active && "from-[#9d86ff] to-[#6c4ce8]",
        className,
      )}
    >
      {active && <SoundRings />}
      <Volume2 className={cn("relative", size === "lg" ? "h-7 w-7" : "h-4 w-4")} />
    </button>
  );
}

export function AudioControls({ text, className }: { text: string; className?: string }) {
  const t = useT();
  const { active, play, slow, stop } = useSpeaker();
  const btn =
    "inline-flex h-11 items-center gap-1.5 rounded-full px-4 text-sm font-bold transition hover:-translate-y-0.5 active:translate-y-0";
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <button
        type="button"
        className={cn(btn, "relative bg-[#e4f5ff] text-[#0e5c8a]", active && "bg-candy text-white")}
        onClick={() => play(text)}
        aria-label={t(t("सामान्य गति", "Normal speed"), "Normal speed")}
      >
        <Volume2 className="h-4 w-4" /> {t("सुनें", "Listen")}
        {active && (
          <span aria-hidden className="ml-0.5 flex h-3 items-end gap-0.5">
            {[0, 1, 2].map((i) => (
              <span key={i} className="w-0.5 animate-bounce rounded-full bg-current" style={{ height: `${6 + i * 3}px`, animationDelay: `${i * 0.12}s` }} />
            ))}
          </span>
        )}
      </button>
      <button type="button" className={cn(btn, "bg-[#e3f9ef] text-[#0d7552]")} onClick={() => slow(text)} aria-label={t(t("धीमी गति", "Slow speed"), "Slow speed")}>
        <Turtle className="h-4 w-4" /> {t("धीमे", "Slow")}
      </button>
      {active ? (
        <button type="button" className={cn(btn, "bg-[#ffedf2] text-[#b3214a]")} onClick={stop} aria-label={t(t("रोकें", "Stop"), "Stop")}>
          <Square className="h-4 w-4 fill-current" /> {t("रोकें", "Stop")}
        </button>
      ) : (
        <button type="button" className={cn(btn, "w-11 justify-center bg-accent px-0 text-accent-foreground")} onClick={() => play(text)} aria-label={t(t("दोहराएँ", "Repeat"), "Repeat")}>
          <RotateCcw className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/** Header player: shown only while something is playing or paused. */
export function NowPlaying({ className }: { className?: string }) {
  const t = useT();
  const { status, speed } = useSyncExternalStore(subscribePlayback, getPlaybackState, () => IDLE_STATE);
  if (status === "idle") return null;
  const playing = status === "playing";
  const nextSpeed = SPEEDS[(SPEEDS.indexOf(speed as (typeof SPEEDS)[number]) + 1) % SPEEDS.length];
  const btn = "grid size-9 place-items-center rounded-full transition hover:scale-105 active:scale-95";
  return (
    <div
      role="group"
      aria-label={t("ऑडियो नियंत्रण", "Audio controls")}
      className={cn("inline-flex items-center gap-1 rounded-full bg-card p-1 shadow-[var(--shadow-card)] animate-in fade-in zoom-in-95 duration-200", className)}
    >
      <button
        type="button"
        onClick={() => (playing ? pauseAudio() : resumeAudio())}
        aria-label={playing ? t("रोकें", "Pause") : t("चलाएँ", "Play")}
        className={cn(btn, "bg-candy text-white shadow-soft")}
      >
        {playing ? <Pause className="size-4 fill-current" /> : <Play className="size-4 fill-current" />}
      </button>
      <button
        type="button"
        onClick={() => setSpeed(nextSpeed)}
        aria-label={t(`गति ${speed}×, बदलें`, `Speed ${speed}×, change`)}
        title={t("गति बदलें", "Change speed")}
        className="h-9 min-w-12 rounded-full px-2 text-sm font-extrabold text-primary transition hover:bg-accent"
      >
        {speed}×
      </button>
      <button
        type="button"
        onClick={() => stopAudio()}
        aria-label={t("बंद करें", "Stop")}
        className={cn(btn, "text-muted-foreground hover:text-[#b3214a]")}
      >
        <X className="size-4" />
      </button>
    </div>
  );
}
